import { useState, useId } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { CloseCircle, InfoCircle } from "iconsax-react";
import { SelectArrowDownIcon } from "@/shared/assets/icons";
import { useHighlightFade } from "@/shared/hooks/useHighlightFade";
import { Dropdown, DropdownItem } from "..";
import styles from "./Select.module.css";

interface Option<T> {
  label: string;
  value: T | null;
  icon?: React.ReactNode;
  tooltip?: string;
}

interface Props<T> {
  label?: string;
  tooltip?: string;
  error?: string;
  options: Option<T>[];
  placeholder?: string;
  value?: T | null;
  onChange?: (value: T | null) => void;
  width?: string;
  disabled?: boolean;
  isSmall?: boolean;
  icon?: React.ReactNode;
  hasOptionalLabel?: boolean;
  hasRequiredLabel?: boolean;
  clearable?: boolean;
  highlight?: boolean;
}

export default function Select<T extends string | number | boolean | null>({
  label,
  tooltip,
  error,
  options,
  placeholder = "Select",
  value,
  onChange,
  width = "w-full",
  disabled = false,
  isSmall = false,
  icon,
  hasOptionalLabel = false,
  hasRequiredLabel = false,
  clearable = false,
  highlight = false,
}: Props<T>) {
  const { t } = useTranslation("Common");
  const inputId = useId();
  const [open, setOpen] = useState(false);
  const showHighlight = useHighlightFade(highlight) && !error;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [hoveredOptionIdx, setHoveredOptionIdx] = useState<number | null>(null);

  const selected = options.find((o) => o.value === value);
  const isPlaceholder = !selected || selected.value === null;

  return (
    <div className={cn("min-w-0", width)}>
      <Dropdown open={open && !disabled} onClose={() => setOpen(false)} fullWidth>
        <div className="flex flex-col gap-2">
        {label && (
          <div className="flex items-center gap-1">
            <label htmlFor={inputId} className={cn("text-label-sm", disabled ? "content-base-low" : "content-base-primary")}>{label}</label>
            {hasRequiredLabel && <span className="content-action-negative">*</span>}
            {hasOptionalLabel && <span className="text-xs content-action-neutral">({t("optional")})</span>}
            {tooltip && (
              <div
                className="relative"
                onMouseEnter={() => setTooltipOpen(true)}
                onMouseLeave={() => setTooltipOpen(false)}>
                <Dropdown
                  open={tooltipOpen}
                  onClose={() => setTooltipOpen(false)}
                  direction="bottom"
                  align="left"
                  width="w-max"
                  className="elevation-level-2!">
                  <span className="content-action-neutral cursor-pointer flex items-center">
                    <InfoCircle size={16} color="currentColor" />
                  </span>
                  <div
                    className="p-2 max-w-48"
                    onMouseEnter={() => setTooltipOpen(true)}
                    onMouseLeave={() => setTooltipOpen(false)}>
                    <p className="text-label-xs content-action-neutral">{tooltip}</p>
                  </div>
                </Dropdown>
              </div>
            )}
          </div>
        )}
        <button
          id={inputId}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((p) => !p)}
          className={cn(
            "w-full flex items-center px-3 surface-base-fill input-box-shadow outline-none gap-2",
            isSmall ? "h-8 radius-xs" : "py-3 radius-sm",
            showHighlight && "input-highlight-box-shadow",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          )}
>
          {icon && <span className="shrink-0 flex items-center text-grey-400">{icon}</span>}
          {!isPlaceholder && selected?.icon && (
            <span className="shrink-0 flex items-center content-base-secondary">{selected.icon}</span>
          )}
          <span
            className={cn(
              "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left",
              isPlaceholder ? cn("text-body-regular-sm", styles.placeholder) : "text-body-regular-sm content-base-primary",
            )}>
            {isPlaceholder ? placeholder : selected?.label}
          </span>
          {clearable && !isPlaceholder ? (
            <span
              role="button"
              className="content-base-secondary hover:content-base-primary w-4 h-4 flex items-center justify-center shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(null);
                setOpen(false);
              }}>
              <CloseCircle size={16} color="currentColor" variant="Bold" />
            </span>
          ) : (
            <span className="content-base-secondary w-4 h-4 flex items-center justify-center shrink-0">
              <SelectArrowDownIcon />
            </span>
          )}
        </button>
        {error && !disabled && <p className="text-body-regular-xs content-action-negative">{error}</p>}
      </div>

        {!disabled && (
          <div className={cn(options.length > 6 && "component-scroll-thin max-h-42")}>
            {options.map((opt, idx) => (
              <div key={opt.value === null ? "__placeholder__" : String(opt.value)} className="relative">
                <DropdownItem
                  active={opt.value === value}
                  onClick={() => {
                    onChange?.(opt.value);
                    setOpen(false);
                  }}
                  className={opt.tooltip ? "pr-7" : undefined}>
                  {opt.icon && <span className="shrink-0 flex items-center">{opt.icon}</span>}
                  {opt.label}
                </DropdownItem>
                {opt.tooltip && (
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 content-action-neutral cursor-help z-10 flex items-center"
                    onMouseEnter={() => setHoveredOptionIdx(idx)}
                    onMouseLeave={() => setHoveredOptionIdx(null)}>
                    <InfoCircle size={14} color="currentColor" />
                    {hoveredOptionIdx === idx && (
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 z-[200] p-2 w-48 surface-base-fill elevation-level-2 radius-xs pointer-events-none">
                        <p className="text-label-xs content-base-secondary whitespace-normal">{opt.tooltip}</p>
                      </div>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Dropdown>
    </div>
  );
}
